import { useMemo } from "react";
import { useAppSelector } from "@/store/hooks";
import type { WalletAsset } from "@/interfaces/asset";
import {
  formatCurrency,
  formatEvmHumanBalance,
  normalizeCurrency,
} from "@/utils";
import { removeTrailingToken } from "@/utils";
import { getAvailableBalance } from "./helpers";

interface Props {
  asset?: WalletAsset;
  title?: string;
}

const SelectedAssetCard = ({ asset, title = "Selected Asset" }: Props) => {
  const { showBalance } = useAppSelector((state) => state.setting);

  const { theme } = useAppSelector((state) => state.ui);

  const availableBalance = useMemo(() => {
    return getAvailableBalance(asset);
  }, [asset]);

  return (
    <div
      className={`rounded-xl border p-4 ${
        theme === "dark"
          ? "bg-muted border-border"
          : "bg-gray-50 border-gray-200"
      }`}
    >
      <p className="text-xs text-muted-foreground mb-3">{title}</p>

      {asset ? (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <img
                src={
                  asset.iso2
                    ? `https://flagcdn.com/w80/${asset.iso2.toLowerCase()}.png`
                    : (asset.token.icon ?? undefined)
                }
                alt={asset.token.name}
                className="h-12 w-12 rounded-full object-cover"
              />

              {!asset.iso2 && (
                <img
                  src={asset.token.blockchainNetwork?.icon ?? undefined}
                  alt={asset.token.blockchainNetwork?.name}
                  className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-background bg-background"
                />
              )}
            </div>

            <div>
              <div className="font-semibold">
                {removeTrailingToken(asset.token.name)}
              </div>

              <div className="text-sm text-muted-foreground">
                {asset.token.symbol} • {asset.token.blockchainNetwork?.name}
              </div>
            </div>
          </div>

          <div className="text-right">
            <div className="text-xs text-muted-foreground">Available</div>

            <div className="font-bold text-emerald-500">
              {showBalance
                ? `${formatEvmHumanBalance(availableBalance, asset?.token?.isNative)} ${normalizeCurrency(asset.currency ?? "")}`
                : "********"}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">
          Select an asset to continue
        </div>
      )}
    </div>
  );
};

export default SelectedAssetCard;
